// 批次上傳音樂的執行：同時最多 3 首，每首沿用單首上傳的流程（runMusicUpload：檢查檔案 → 量長度 → 一個請求傳完），
// 失敗的只影響那一首、可以重試。musicBatchUpload.test.ts 用假的 deps 測。
//
// 安全：已經有音樂的歌絕不能被蓋掉（換音樂會刪舊檔，之後要拿舊檔當比對基準）。
//   送出前問後台「這首歌現在有沒有音樂」，有 → 跳過（不管畫面上的歌單是不是舊的）。
import { getSongMusic, musicErrorText, type SongMusic, type SongMusicSummary } from './musicApi';
import { runMusicUpload, type UploadDeps } from './songMusicUpload';

export const BATCH_CONCURRENCY = 3;

export type ItemState =
  | { kind: 'queued' }
  | { kind: 'checking' }
  | { kind: 'uploading'; progress: number }
  | { kind: 'done'; music: SongMusic }
  | { kind: 'skipped'; reason: 'hasMusic' }
  | { kind: 'cancelled' }
  | { kind: 'failed'; text: string };

export interface BatchItem {
  key: string;
  songId: string;
  file: File;
}

export interface BatchDeps {
  /** 這首歌現在的音樂（沒有 = null） */
  fetchExisting: (projectId: string, songId: string) => Promise<SongMusicSummary | null>;
  uploadDeps?: Partial<UploadDeps>;
}

const realDeps: BatchDeps = {
  fetchExisting: (p, s) => getSongMusic(p, s),
};

/** 傳一首。已有音樂 → 'skipped'（不呼叫上傳） */
export async function runBatchItem(o: {
  projectId: string;
  item: BatchItem;
  signal?: AbortSignal;
  onState: (key: string, s: ItemState) => void;
  deps?: Partial<BatchDeps>;
}): Promise<ItemState> {
  const d = { ...realDeps, ...o.deps };
  const { item, projectId } = o;
  const set = (s: ItemState): ItemState => { o.onState(item.key, s); return s; };
  if (o.signal?.aborted) return set({ kind: 'cancelled' });
  set({ kind: 'checking' });
  let existing: SongMusicSummary | null;
  try {
    existing = await d.fetchExisting(projectId, item.songId);
  } catch (e) {
    return set({ kind: 'failed', text: musicErrorText(e) });
  }
  if (existing) return set({ kind: 'skipped', reason: 'hasMusic' });
  if (o.signal?.aborted) return set({ kind: 'cancelled' });
  const out = await runMusicUpload({
    projectId, songId: item.songId, file: item.file,
    existing: null,   // 前面已經確認沒有音樂；絕不走「換音樂」
    signal: o.signal,
    onPhase: (p) => { if (p === 'uploading') set({ kind: 'uploading', progress: 0 }); },
    onProgress: (l, t) => set({ kind: 'uploading', progress: t > 0 ? l / t : 0 }),
    deps: { ...d.uploadDeps },
  });
  switch (out.kind) {
    case 'done': return set({ kind: 'done', music: out.music });
    case 'cancelled': return set({ kind: 'cancelled' });
    case 'rejected': case 'unreadable': case 'failed': return set({ kind: 'failed', text: out.text });
    default: return set({ kind: 'failed', text: '沒有傳成功' });
  }
}

/** 一批一起傳，同時最多 limit 首；全部結束才回來 */
export async function runBatch(o: {
  projectId: string;
  items: BatchItem[];
  limit?: number;
  signal?: AbortSignal;
  onState: (key: string, s: ItemState) => void;
  deps?: Partial<BatchDeps>;
}): Promise<Map<string, ItemState>> {
  const results = new Map<string, ItemState>();
  const queue = [...o.items];
  for (const it of queue) o.onState(it.key, { kind: 'queued' });
  const worker = async () => {
    while (queue.length) {
      const item = queue.shift() as BatchItem;
      const r = await runBatchItem({ projectId: o.projectId, item, signal: o.signal, onState: o.onState, deps: o.deps });
      results.set(item.key, r);
    }
  };
  const n = Math.max(1, Math.min(o.limit ?? BATCH_CONCURRENCY, queue.length || 1));
  await Promise.all(Array.from({ length: n }, worker));
  return results;
}

/** 結果統計：成功幾首、失敗幾首、跳過幾首 */
export function summarizeStates(states: Iterable<ItemState>): { done: number; failed: number; skipped: number; cancelled: number } {
  const c = { done: 0, failed: 0, skipped: 0, cancelled: 0 };
  for (const s of states) {
    if (s.kind === 'done') c.done++;
    else if (s.kind === 'failed') c.failed++;
    else if (s.kind === 'skipped') c.skipped++;
    else if (s.kind === 'cancelled') c.cancelled++;
  }
  return c;
}
